import React, { Component } from 'react';
import { ApexUIProvider } from '@apex/component-library';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ALL_REQUESTED_PERMISSIONS, ROUTER_BASENAME } from 'common';
import ReduxToastr from 'react-redux-toastr';
import { Alert } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import { legit } from 'apex-legit-service';
import OktaSignInWidget from '@okta/okta-signin-widget';
import { trackPage } from 'utils';
import classNames from 'classnames';
import { menuItems, menuPermissions } from 'apex-online-menu';
import { LEGIT_SERVICE_CONFIG } from 'containers/Login';
import AppHeader from 'components/AppHeader';
import AppFooter from 'components/AppFooter';
import CenteredAppHeader from 'components/CenteredAppHeader';
import { selectResultsAmountTotal, totalSize, selectSearchShowAllFields } from 'services/search/selectors';
import { selectFilteredResultsCount } from 'containers/Search';
import { oktaConfig } from '../../utils/okta-config';
import messages from './messages';
import styles from './styles.css';
import { selectErrors, selectOrgUnits, selectShowLoginUI, selectShowAppContent } from './selectors';
import { toggleAppErrorVisibility, clearAllErrors } from './actions';
import { selectStatus as ssoLoginStatus } from '../../services/SsoLogin/selectors';
import { SUCCESS as ssoLoginSuccess } from '../../services/SsoLogin/constants';
import { clearRumUser, setRumSessionUser } from '../../utils/datadog';
import LoginModule from '../Login/LoginModule';

export class App extends Component {
  static getDerivedStateFromProps(props, state) {
    const { location, orgUnits } = state;
    const currentPage = location.pathname;
    const nextPage = props.location.pathname;
    const nextOrgUnits = props.orgUnits;

    if (currentPage !== nextPage || orgUnits !== nextOrgUnits) {
      if (isArray(nextOrgUnits) && nextOrgUnits.length > 0) {
        const pathName = nextPage === '/' ? '/search' : nextPage;
        trackPage(`${ROUTER_BASENAME}${pathName}`, nextOrgUnits);
      }
    }
    return {
      orgUnits: props.orgUnits,
      location: props.location,
    };
  }

  constructor(props) {
    super(props);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.state = {
      /* eslint-disable react/no-unused-state */
      // This state is used in getDerivedStateFromProps, but linter doesn't understand that.
      location: this.props.location,
      orgUnits: this.props.orgUnits,
      /* eslint-enable react/no-unused-state */
    };
    this.oktaWidget = {};
  }

  componentDidMount() {
    // Attempt to adopt an existing session without requiring login
    this.props.adopt(LEGIT_SERVICE_CONFIG, ALL_REQUESTED_PERMISSIONS);

    // Okta Widget shared to <Login />
    this.oktaWidget = new OktaSignInWidget(oktaConfig);
  }

  componentDidUpdate(prevProps) {
    if (this.props.ssoLoginRequestStatus === ssoLoginSuccess && prevProps.ssoLoginRequestStatus !== ssoLoginSuccess) {
      this.props.adopt(LEGIT_SERVICE_CONFIG, ALL_REQUESTED_PERMISSIONS);
    }
    if (this.props.showLogout === true && prevProps.showLogout === false) {
      this.props.menuPermissionsRequest(LEGIT_SERVICE_CONFIG.apiUrl);
      this.props.menuItemsRequest(LEGIT_SERVICE_CONFIG.apiUrl);
    }
    if (this.props.location.search !== prevProps.location.search) {
      this.props.clearAllErrors();
    }
    if (this.props.showAppContent) {
      setRumSessionUser();
    }
  }

  componentWillUnmount() {
    this.props.clearAllErrors();
  }

  handleDismiss(event) {
    if (!isObject(event)) return;

    const alert = event.currentTarget.parentNode;
    const errorId = alert.getAttribute('data-error-id');

    if (!errorId) return;

    this.props.toggleAppErrorVisibility(errorId);
  }

  handleLogout() {
    const params = { apexUser: false };
    this.props.logout(LEGIT_SERVICE_CONFIG, params);
    this.oktaWidget.authClient.signOut();
    clearRumUser();
  }

  renderError() {
    const { errors } = this.props;
    const currentError = errors[errors.length - 1];
    if (!currentError || !currentError.visible) {
      return null;
    }
    let errorMessage = '';
    const { error, id } = currentError;
    if (isString(error)) {
      errorMessage = error;
    } else if (isObject(error) && isString(error.errorDescription)) {
      const { errorDescription } = error;
      errorMessage = errorDescription;
    } else if (isObject(error) && isString(error.message)) {
      const { message } = error;
      errorMessage = message;
    }

    return (
      errorMessage && (
        <Alert key={`error-${id}`} bsStyle="danger" data-error-id={`${id}`} onDismiss={this.handleDismiss}>
          <div className="error error-header">Error</div>
          <div className="error error-body">Something is wrong - {errorMessage}</div>
        </Alert>
      )
    );
  }
  render() {
    // eslint-disable-next-line no-console
    const {
      isSessionLoading,
      isActiveLink,
      filteredResultsCount,
      links,
      showLogin,
      showLogout,
      totalDataSetSize,
      resultsAmountTotal,
      location,
      showAppContent,
      hasFullTransfersPermission,
      menuItemsResponse,
      menuPermissionsResponse,
    } = this.props;
    const isSearchPage = location.pathname === '/search';
    const extraStyles = isSearchPage ? styles.searchPage : '';
    return (
      <div className={styles.app}>
        {showLogin ?
          <CenteredAppHeader /> :
          <AppHeader
            title={<FormattedMessage {...messages.title} />}
            links={links}
            isActiveLink={isActiveLink}
            showLogout={showLogout}
            onLogoutClick={this.handleLogout}
            showSubMenu={hasFullTransfersPermission}
            menuItems={menuItemsResponse}
            menuPermissions={menuPermissionsResponse}
          />
        }
        <div className={classNames(styles.appContent, extraStyles)}>
          {this.renderError()}
          {isSessionLoading && (
            <span>
              <FormattedMessage {...messages.loading} />
            </span>
          )}
          {showLogin && <ApexUIProvider><LoginModule /></ApexUIProvider>}
          {showAppContent && this.props.render()}
        </div>
        <ReduxToastr newestOnTop preventDuplicates position="top-right" transitionIn="fadeIn" transitionOut="fadeOut" />
        <AppFooter
          filteredResultsCount={filteredResultsCount}
          resultsCount={totalDataSetSize}
          resultsAmountTotal={resultsAmountTotal}
          isSearchPage={isSearchPage}
          showAppContent={showAppContent}
        />
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  errors: selectErrors(state),
  isSessionLoading: legit.selectors.isSessionLoading(state),
  legitError: legit.selectors.selectLegitError(state),
  orgUnits: selectOrgUnits(state),
  showLogin: selectShowLoginUI(state),
  showAppContent: selectShowAppContent(state),
  showLogout: legit.selectors.selectShowLogoutUI(state),
  filteredResultsCount: selectFilteredResultsCount(state),
  resultsAmountTotal: selectResultsAmountTotal(state),
  totalDataSetSize: totalSize(state),
  ssoLoginRequestStatus: ssoLoginStatus(state),
  hasFullTransfersPermission: selectSearchShowAllFields(state),
  menuItemsResponse: menuItems.selectors.menuItemsResults(state),
  menuPermissionsResponse: menuPermissions.selectors.menuPermissionsResults(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      adopt: legit.adoptExistingSession,
      login: legit.login,
      logout: legit.logout,
      toggleAppErrorVisibility,
      clearAllErrors,
      menuItemsRequest: menuItems.request,
      menuPermissionsRequest: menuPermissions.request,
    },
    dispatch,
  );

App.propTypes = {
  adopt: PropTypes.func,
  errors: PropTypes.array,
  isActiveLink: PropTypes.func,
  isSessionLoading: PropTypes.bool,
  legitError: PropTypes.string,
  links: PropTypes.array,
  location: PropTypes.object,
  login: PropTypes.func,
  logout: PropTypes.func,
  orgUnits: PropTypes.array,
  render: PropTypes.func.isRequired,
  showLogin: PropTypes.bool.isRequired,
  showLogout: PropTypes.bool,
  showAppContent: PropTypes.bool.isRequired,
  filteredResultsCount: PropTypes.number.isRequired,
  resultsAmountTotal: PropTypes.string.isRequired,
  toggleAppErrorVisibility: PropTypes.func,
  clearAllErrors: PropTypes.func,
  ssoLoginRequestStatus: PropTypes.string,
  hasFullTransfersPermission: PropTypes.bool,
  totalDataSetSize: PropTypes.number,
  menuItemsRequest: PropTypes.func,
  menuPermissionsRequest: PropTypes.func,
  menuItemsResponse: PropTypes.array,
  menuPermissionsResponse: PropTypes.any,
};

App.defaultProps = {
  adopt: () => {},
  errors: [],
  isActiveLink: () => false,
  isSessionLoading: false,
  legitError: '',
  links: [],
  location: { pathname: '' },
  login: () => {},
  logout: () => {},
  orgUnits: [],
  showLogout: false,
  toggleAppErrorVisibility: () => {},
  clearAllErrors: () => {},
  ssoLoginRequestStatus: '',
  hasFullTransfersPermission: false,
  totalDataSetSize: 0,
  menuItemsResponse: [],
  menuPermissionsResponse: [],
  menuItemsRequest: () => false,
  menuPermissionsRequest: () => false,
};

export * from './constants';
export { default as reducer } from './reducer';
export * from './sagas';
export default connect(mapStateToProps, mapDispatchToProps)(App);



// WEBPACK FOOTER //
// ./src/containers/App/index.js